/**
 * Learn Mode — the sandbox.
 *
 * While a lesson is running, the real pages must render a pretend deal and no
 * request may reach the server. This installs a fixture adapter and, critically,
 * installs it on the GLOBAL axios default as well as our own client — because six
 * signed-URL upload sites do `import axios from 'axios'` and call
 * `axios.put(uploadUrl, file)` on the default instance, which our client's
 * adapter would never see.
 *
 * IT FAILS CLOSED. Anything not in the route table is REJECTED, not passed
 * through: an unmatched mutation is a bug in the fixtures, and the right
 * behaviour for a bug in a sandbox is a loud error, never a real write.
 *
 * Two escape paths remain that no adapter can close, and the tour is designed
 * around them rather than pretending otherwise:
 *   · the Copilot stream is a raw `fetch()` + ReadableStream
 *   · sale-note attachments upload with a raw `fetch()` PUT
 * Neither is on any lesson's path, and the overlay's blockers make every control
 * except the spotlit one unclickable, so an agent cannot reach them mid-lesson.
 */
import axios from 'axios';
import client from '../api/client';
import { resolve } from './fixtures';

let ON = false;
export const isDemo = () => ON;

const envelope = (data, pagination) => ({
  success: true,
  message: 'Data retrieved successfully',
  ...(data === undefined ? {} : { data }),
  ...(pagination ? { pagination } : {}),
});

const reply = (config, body) => ({
  data: body, status: 200, statusText: 'OK', headers: {}, config,
});

function blocked(config) {
  // A refused write inside a lesson is ALWAYS a fixture bug — the overlay only
  // lets the agent reach controls a step pointed them at, so anything they can
  // press is something the lesson meant them to press. It surfaces to the agent
  // as "not available in a practice lesson", which reads like a dead end and
  // tells whoever is building the lesson nothing. Say what was refused.
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn(`[learn] sandbox refused ${String(config.method || 'get').toUpperCase()} ${config.url} — add it to fixtures.js resolve(), or the step that triggers it will dead-end.`);
  }
  const e = new Error('Learn Mode is running — this action is not available in a practice lesson.');
  e.code = 'LEARN_SANDBOX_BLOCKED';
  e.config = config;
  return Promise.reject(e);
}

function demoAdapter(fallback) {
  return async (config) => {
    if (!ON) return fallback(config);

    const method = String(config.method || 'get').toUpperCase();
    const url = String(config.url || '');
    const path = url.split('?')[0];

    // Anything that is not our API while a lesson runs is a storage upload, a
    // signed-URL fetch or a third party. None of it belongs in a practice deal.
    if (!path.includes('/api/agent/')) return blocked(config);

    const hit = resolve(method, path, config);
    if (hit) {
      // A beat of latency so skeletons appear and the tour looks like the app.
      await new Promise((r) => setTimeout(r, 160));
      // A `raw` hit is returned as-is. The invoice PDF is fetched with
      // responseType:'blob', so wrapping it in the JSON envelope would hand
      // URL.createObjectURL a plain object and the viewer would render nothing.
      if (hit.raw) return reply(config, hit.data);
      return reply(config, hit.paginated ? envelope(hit.data, hit.pagination) : envelope(hit.data));
    }

    // Unmatched GETs degrade to an empty payload so a screen the lesson merely
    // passes through never errors. Unmatched writes are refused outright.
    if (method === 'GET') {
      await new Promise((r) => setTimeout(r, 120));
      const listish = /(list|search|options)$/.test(path) || /s$/.test(path);
      return reply(config, listish
        ? envelope([], { page: 1, limit: 20, total: 0, totalPages: 1 })
        : envelope({}));
    }
    return blocked(config);
  };
}

let savedClient = null;
let savedGlobal = null;

export function setDemoMode(on) {
  if (on === ON) return;
  ON = on;
  if (on) {
    savedClient = client.defaults.adapter;
    savedGlobal = axios.defaults.adapter;
    const base = savedClient || axios.getAdapter('xhr');
    client.defaults.adapter = demoAdapter(base);
    // The bare-axios upload sites live here.
    axios.defaults.adapter = demoAdapter(savedGlobal || axios.getAdapter('xhr'));
  } else {
    client.defaults.adapter = savedClient;
    axios.defaults.adapter = savedGlobal;
    savedClient = null;
    savedGlobal = null;
  }
}
